'use client';

import React, { useState, useCallback } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/context/AuthContext';
import { googleLogin } from '@/lib/api-client';
import { useGoogleClientId } from './GoogleAuthWrapper';

interface GoogleSignInButtonProps {
  returnTo?: string;
  onError?: (msg: string) => void;
  label?: string;
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function GoogleButtonDisabled({ label }: { label: string }) {
  return (
    <button
      type="button"
      disabled
      className="flex items-center justify-center gap-2 bg-card-bg border border-default rounded-xl py-2.5 text-sm text-fg-subtle cursor-not-allowed opacity-60"
    >
      <GoogleIcon />
      {label}
    </button>
  );
}

function GoogleButtonActive({ returnTo, onError, label }: Required<Pick<GoogleSignInButtonProps, 'returnTo' | 'label'>> & { onError?: (msg: string) => void }) {
  const { loginWithPayload } = useAuth();
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const handleSuccess = useCallback(async (accessToken: string) => {
    setLoading(true);
    try {
      const payload = await googleLogin(accessToken);
      loginWithPayload(payload.user, payload.accessToken, payload.showTrialPopup, payload.trialPopupTitle, payload.trialPopupMessage);
      router.push(returnTo);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Google sign-in failed.';
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  }, [loginWithPayload, router, returnTo, onError]);

  const googleOAuth = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      void handleSuccess(tokenResponse.access_token);
    },
    onError: () => {
      onError?.('Google sign-in was cancelled.');
    },
  });

  return (
    <button
      type="button"
      disabled={loading}
      onClick={() => googleOAuth()}
      className="flex items-center justify-center gap-2 bg-card-bg border border-default hover:bg-card-hover-bg hover:border-muted rounded-xl py-2.5 text-sm text-slate-300 transition-colors disabled:opacity-60"
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : <GoogleIcon />}
      {label}
    </button>
  );
}

export default function GoogleSignInButton({ returnTo = '/', onError, label = 'Google' }: GoogleSignInButtonProps) {
  const clientId = useGoogleClientId();

  if (!clientId) {
    return null;
  }

  return <GoogleButtonActive returnTo={returnTo} onError={onError} label={label} />;
}
